import { useEffect, useMemo, useState } from 'react'
import DateFnsUtils from '@date-io/date-fns';
import GeneralModal from './GeneralModal';
import { Button, Grid, TextField, Typography, makeStyles } from '@material-ui/core';
import { KeyboardDatePickerInput, PrimaryButton } from '../components-atoms';
import { Autocomplete } from '@material-ui/lab';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ITheme } from '../constants/theme';
import { InvoiceMerchant } from '../redux/reducers/getInvoiceMerchants';
import { textButtonHoverAndActive } from '../utils/styles/buttonStyles';
import { useDispatch } from 'react-redux';
import { getInvoiceMerchants } from '../redux/actions/getInvoiceMerchants';

type OnChangeKeyType = 'DateFrom' | 'DateTo' | 'Merchant';
interface IBody {
  DateFrom: Date | null;
  DateTo: Date | null;
  Merchant: {
    Id: number;
    Name: string;
  }
}

interface IAgencyFeeExportModalProps {
  visible: boolean;
  isAdmin?: boolean;
  merchants?: InvoiceMerchant[];
  onExport: (body: IBody) => void;
  onClose: () => void;
}

const defaultOption = { Id: 0, Name: 'All Merchants' };
const defaultBody = {
  DateFrom: null,
  DateTo: null,
  Merchant: defaultOption
};
export default function AgencyFeeExportModal({ isAdmin, visible, merchants, onExport, onClose }: IAgencyFeeExportModalProps) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [body, setBody] = useState<IBody>(defaultBody);
  const [localMerchants, setLocalMerchants] = useState(merchants);
  const isDisabled = useMemo(() => {
    if (isAdmin) {
      return !(body.DateFrom && body.DateTo && body.Merchant);
    }
    return !(body.DateFrom && body.DateTo);
  }, [body.DateTo, body.Merchant, body.DateFrom, isAdmin])
  const memoizedMerchants = useMemo(() => merchants, [merchants]);

  useEffect(() => {
    if (isAdmin && visible && (!memoizedMerchants || memoizedMerchants?.length === 0)) {
      dispatch(getInvoiceMerchants((res) => {
        if (res?.Result?.length > 0) {
          setLocalMerchants(res.Result);
        }
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, visible, memoizedMerchants?.length, isAdmin]);

  const onChange = (key: OnChangeKeyType, value: string | Date | null) => {
    setBody({
      ...body,
      [key]: value
    });
  };

  const onSubmit = () => {
    onExport(body);
    setBody(defaultBody);
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <GeneralModal
        title="CSV export"
        description={isAdmin ? "Select your desired start and end dates for your export. Optionally, you can designate a specific merchant to export its data exclusively."
          : "Select your desired start and end dates for your export."}
        modalWidth="560px"
        visible={visible}>
        <GeneralModal.ContentArea>
          <Grid container style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              {isAdmin ?
                <Autocomplete
                  id="merchant-dropdown"
                  options={[defaultOption, ...localMerchants as any]}
                  getOptionLabel={(option: any) => option.Name}
                  value={body.Merchant}
                  onChange={(e: any, v: any) => onChange('Merchant', v)}
                  renderInput={(params) => <TextField {...params} label="Merchant Name" variant="outlined" />}
                />
                :
                <></>
              }
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={6}>
                <KeyboardDatePickerInput
                  placeholder="DD/MM/YYYY"
                  label="Start Date"
                  onChange={(d) => onChange('DateFrom', d)}
                  value={body.DateFrom}
                  maxDate={body.DateTo}
                />
              </Grid>
              <Grid item xs={6}>
                <KeyboardDatePickerInput
                  placeholder="DD/MM/YYYY"
                  label="End Date"
                  onChange={(d) => onChange('DateTo', d)}
                  value={body.DateTo}
                  minDate={body.DateFrom}
                  minDateMessage="End date should be after the start date"
                />
              </Grid>
            </Grid>
          </Grid>
        </GeneralModal.ContentArea>
        <GeneralModal.ButtonArea>
          <Button
            className={classes.cancelButton}
            onClick={() => {
              onClose();
              setBody(defaultBody);
            }}>
            <Typography
              variant="button"
              component="p"
              color="primary">
              Cancel
            </Typography>
          </Button>
          <PrimaryButton
            title="Export"
            disabled={Boolean(isDisabled)}
            onClick={onSubmit}
            style={{ marginLeft: '8px' }}
          />
        </GeneralModal.ButtonArea>
      </GeneralModal>
    </MuiPickersUtilsProvider >
  )
}

const useStyles = makeStyles((theme: ITheme) => ({
  cancelButton: {
    padding: '12px 16px',
    borderRadius: '8px',
    color: theme.palette.primary.main,
    ...textButtonHoverAndActive
  }
}));